import { FC, ReactNode, useState } from 'react'
import { Indicator } from '@mantine/core'
import dayjs from 'dayjs'
import styles from '../components/atoms/Calendar/styles'
import { SeatBooking } from '../types/andTypes'
import { formatDate, formatToReadableDate, isDisabledDate } from '../utilities/utils'
import { andTheme } from '../styles/theme'

type CalendarIndicatorProps = {
  label: string
  children: ReactNode
}

const CalendarIndicator: FC<CalendarIndicatorProps> = ({ label, children }) => {
  const [hovered, setHovered] = useState(false)

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <Indicator label={label} disabled={!hovered} styles={styles}>
        {children}
      </Indicator>
    </div>
  )
}

const useCalendar = (seatCap: number) => {
  const renderDay = (date: Date, seatsBooking: SeatBooking[]) => {
    for (let i = 0; i < seatsBooking.length; i++) {
      const isSeatsBookedOnDate = formatDate(date) === formatDate(seatsBooking[i].dateBooked)
      const seatsAvailable = seatCap - seatsBooking[i].seats?.length

      const seatsText = seatsAvailable === 1 ? 'desk' : 'desks'

      if (isSeatsBookedOnDate) {
        return (
          <CalendarIndicator label={`${seatsAvailable}/${seatCap}`}>
            <span
              title={`${seatsAvailable} ${seatsText} available on ${formatToReadableDate(date)}`}
            >
              {date.getDate()}
            </span>
          </CalendarIndicator>
        )
      }
    }

    return (
      <CalendarIndicator label={`${seatCap}/${seatCap}`}>
        <span title={`${seatCap} desks available on ${formatToReadableDate(date)}`}>
          {date.getDate()}
        </span>
      </CalendarIndicator>
    )
  }

  const renderStyle = (date: Date, seatsBooking: SeatBooking[], bankHolidays: string[]) => {
    if (isDisabledDate(date, bankHolidays)) return {}
    if (
      date < dayjs(new Date()).subtract(1, 'day').toDate() ||
      date > dayjs(new Date()).add(4, 'weeks').toDate()
    ) {
      return {}
    }

    for (let i = 0; i < seatsBooking.length; i++) {
      const isSeatsBookedOnDate = formatDate(date) === formatDate(seatsBooking[i].dateBooked)

      if (isSeatsBookedOnDate && seatsBooking[i].seats.length === seatCap) {
        return {
          backgroundColor: andTheme.colours.red,
          cursor: 'pointer'
        }
      }
    }

    return { backgroundColor: andTheme.colours.green }
  }

  return { renderDay, renderStyle }
}

export default useCalendar
